import React, { useState, useEffect } from "react";
import { Form, Input, Select, Button, Row, Col, Space } from "antd";
import { SearchOutlined, UndoOutlined } from "@ant-design/icons";
import { useUld } from "../context/UldContext";
import { useUldAirports } from "../../hooks/useUldAirports";
import { 
    ULD_TYPES, 
    ULD_OWNERSHIP_TYPE_OPTIONS 
} from "../constants/uldConstants";
import { 
    ULD_CORE_STATUS_NAMES,
    ULD_AVAILABILITY_NAMES 
} from "../constants/uldConstantsV3";
import { 
    getOwnerOptions, 
    getHolderOptions, 
    getGhaOptions, 
    getTagNameOptions 
} from "../../../../api/uldApi";

const { Option } = Select;
const { TextArea } = Input;

const UldFilter = () => {
    const [form] = Form.useForm();
    const { updateFilters, resetFilters, loading } = useUld();
    const { airportsList, loading: airportsLoading, error: airportsError, refreshAirports } = useUldAirports();
    const [expanded, setExpanded] = useState(false);
    
    // 筛选选项数据状态
    const [filterOptions, setFilterOptions] = useState({
        owners: [],
        holders: [],
        ghas: [],
        tagNames: [],
    });
    const [optionsLoading, setOptionsLoading] = useState(false);

    // 获取筛选选项数据
    const fetchFilterOptions = async () => {
        setOptionsLoading(true);
        try {
            const [ownersRes, holdersRes, ghasRes, tagNamesRes] = await Promise.all([
                getOwnerOptions(),
                getHolderOptions(),
                getGhaOptions(),
                getTagNameOptions(),
            ]);

            setFilterOptions({
                owners: ownersRes.data || [],
                holders: holdersRes.data || [],
                ghas: ghasRes.data || [],
                tagNames: tagNamesRes.data || [],
            });
        } catch (error) {
            console.error("获取筛选选项失败:", error);
        } finally {
            setOptionsLoading(false);
        }
    };

    // 组件挂载时获取筛选选项
    useEffect(() => {
        fetchFilterOptions();
    }, []);

    // 处理筛选表单提交
    const handleFilter = () => {
        const values = form.getFieldsValue();
        
        // 构建筛选参数 - 确保所有字段都被处理，包括空值
        const filters = {};
        
        // 处理ULD识别码筛选条件
        if (values.uld_identifiers) {
            // 将文本区域的内容按行分割，过滤空行和空白字符
            const identifiers = values.uld_identifiers
                .split(/[\n,;\s]+/)  // 按换行符、逗号、分号或空白字符分割
                .map(id => id.trim())  // 去除每个识别码的前后空白
                .filter(id => id.length > 0);  // 过滤空字符串
            
            filters.uld_identifiers = identifiers.length > 0 ? identifiers.join(",") : null;
        } else {
            filters.uld_identifiers = null;
        }
        
        // 核心筛选项 - 始终设置这些字段，即使为空
        filters.airport_iatas = (values.airport_iatas && values.airport_iatas.length > 0) ? values.airport_iatas.join(",") : null;
        filters.core_statuses = (values.core_statuses && values.core_statuses.length > 0) ? values.core_statuses.join(",") : null;
        filters.availabilities = (values.availabilities && values.availabilities.length > 0) ? values.availabilities.join(",") : null;
        filters.delivery_status = values.delivery_status || null;
        filters.uld_types = (values.uld_types && values.uld_types.length > 0) ? values.uld_types.join(",") : null;
        filters.owners = (values.owners && values.owners.length > 0) ? values.owners.join(",") : null;
        filters.holders = (values.holders && values.holders.length > 0) ? values.holders.join(",") : null;
        
        // 展开筛选项 - 始终设置这些字段，即使为空
        filters.ghas = (values.ghas && values.ghas.length > 0) ? values.ghas.join(",") : null;
        filters.ownership_types = (values.ownership_types && values.ownership_types.length > 0) ? values.ownership_types.join(",") : null;
        filters.tag_names = (values.tag_names && values.tag_names.length > 0) ? values.tag_names.join(",") : null;
        
        // 备注查询 - 始终设置该字段，即使为空
        filters.remarks_keyword = (values.remarks_keyword && values.remarks_keyword.trim()) ? values.remarks_keyword.trim() : null;

        updateFilters(filters);
    };

    // 重置筛选
    const handleReset = () => {
        form.resetFields();
        resetFilters();
    };

    // 切换展开/折叠状态
    const toggleExpanded = () => {
        setExpanded(!expanded);
    };

    return (
        <Form form={form} layout="vertical" onFinish={handleFilter}>
            {/* 第一行：ULD识别码查询 */}
            <Row gutter={[16, 8]}>
                <Col span={24}>
                    <Form.Item
                        name="uld_identifiers"
                        label="ULD识别码查询"
                        style={{ marginBottom: 8 }}
                    >
                        <TextArea 
                            placeholder="输入ULD识别码，支持多个&#10;可用换行、逗号、分号或空格分隔&#10;例如：&#10;AKE12345XX&#10;PMC67890YY,AKE11111ZZ"
                            autoSize={{ minRows: 2, maxRows: 4 }}
                            allowClear
                        />
                    </Form.Item>
                </Col>
            </Row>

            {/* 第二行：机场、核心状态、可用性 */}
            <Row gutter={[16, 8]}>
                <Col span={8}>
                    <Form.Item
                        name="airport_iatas"
                        label="机场"
                        style={{ marginBottom: 8 }}
                    >
                        <Select
                            mode="multiple"
                            placeholder="选择机场"
                            allowClear
                            showSearch
                            loading={airportsLoading}
                            filterOption={(input, option) => {
                                // 获取机场代码和名称用于搜索
                                const airport = airportsList.find(a => a.iata_code === option.value);
                                if (!airport) return false;
                                
                                const searchText = input.toLowerCase();
                                const iataCode = (airport.iata_code || "").toLowerCase();
                                const nameCn = (airport.name_cn || "").toLowerCase();
                                const nameEn = (airport.name_en || "").toLowerCase();
                                
                                return iataCode.includes(searchText) || 
                                       nameCn.includes(searchText) || 
                                       nameEn.includes(searchText);
                            }}
                        >
                            {airportsList.map(airport => (
                                <Option key={airport.iata_code} value={airport.iata_code}>
                                    {airport.iata_code}{(airport.name_cn || airport.name_en) ? ` - ${airport.name_cn || airport.name_en}` : ''}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        name="core_statuses"
                        label="核心状态"
                        style={{ marginBottom: 8 }}
                    >
                        <Select
                            mode="multiple"
                            placeholder="选择核心状态"
                            allowClear
                        >
                            {Object.entries(ULD_CORE_STATUS_NAMES).map(([key, value]) => (
                                <Option key={key} value={key}>
                                    {value}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        name="availabilities"
                        label="可用性"
                        style={{ marginBottom: 8 }}
                    >
                        <Select
                            mode="multiple"
                            placeholder="选择可用性"
                            allowClear
                        >
                            {Object.entries(ULD_AVAILABILITY_NAMES).map(([key, value]) => (
                                <Option key={key} value={key}>
                                    {value}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>

            {/* 第三行：外放状态、ULD类型、所有者 */}
            <Row gutter={[16, 8]}>
                <Col span={8}>
                    <Form.Item
                        name="delivery_status"
                        label="外放状态"
                        style={{ marginBottom: 8 }}
                    >
                        <Select
                            placeholder="选择外放状态"
                            allowClear
                        >
                            <Option value="all">全部</Option>
                            <Option value="delivered">已外放</Option>
                            <Option value="not_delivered">未外放</Option>
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        name="uld_types"
                        label="ULD类型"
                        style={{ marginBottom: 8 }}
                    >
                        <Select
                            mode="multiple"
                            placeholder="选择ULD类型"
                            allowClear
                            showSearch
                            filterOption={(input, option) => {
                                const value = option.value || "";
                                return value.toLowerCase().includes(input.toLowerCase());
                            }}
                        >
                            {ULD_TYPES.map(type => (
                                <Option key={type} value={type}>
                                    {type}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        name="owners"
                        label="所有者"
                        style={{ marginBottom: 8 }}
                    >
                        <Select
                            mode="multiple"
                            placeholder="选择所有者"
                            allowClear
                            showSearch
                            loading={optionsLoading}
                            filterOption={(input, option) => {
                                const value = option.value || "";
                                return value.toLowerCase().includes(input.toLowerCase());
                            }}
                        >
                            {filterOptions.owners.map(owner => (
                                <Option key={owner} value={owner}>
                                    {owner}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>

            {/* 可展开的高级筛选项 */}
            {expanded && (
                <>
                    {/* 第四行：地面处理代理、所有权类型、标签 */}
                    <Row gutter={[16, 8]}>
                        <Col span={8}>
                            <Form.Item
                                name="ghas"
                                label="地面处理代理"
                                style={{ marginBottom: 8 }}
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="选择地面处理代理"
                                    allowClear
                                    showSearch
                                    loading={optionsLoading}
                                    filterOption={(input, option) => {
                                        const value = option.value || "";
                                        return value.toLowerCase().includes(input.toLowerCase());
                                    }}
                                >
                                    {filterOptions.ghas.map(gha => (
                                        <Option key={gha} value={gha}>
                                            {gha}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="ownership_types"
                                label="所有权类型"
                                style={{ marginBottom: 8 }}
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="选择所有权类型"
                                    allowClear
                                >
                                    {ULD_OWNERSHIP_TYPE_OPTIONS.map(option => (
                                        <Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="tag_names"
                                label="标签"
                                style={{ marginBottom: 8 }}
                            >
                                <Select 
                                    mode="multiple" 
                                    placeholder="选择标签"
                                    allowClear 
                                    showSearch
                                    loading={optionsLoading}
                                    filterOption={(input, option) => {
                                        const value = option.value || "";
                                        return value.toLowerCase().includes(input.toLowerCase());
                                    }}
                                >
                                    {filterOptions.tagNames.map(tagName => (
                                        <Option key={tagName} value={tagName}>
                                            {tagName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* 第五行：持有者 */}
                    <Row gutter={[16, 8]}>
                        <Col span={8}>
                            <Form.Item
                                name="holders"
                                label="持有者"
                                style={{ marginBottom: 8 }}
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="选择持有者"
                                    allowClear
                                    showSearch
                                    loading={optionsLoading}
                                    filterOption={(input, option) => {
                                        const value = option.value || "";
                                        return value.toLowerCase().includes(input.toLowerCase());
                                    }}
                                >
                                    {filterOptions.holders.map(holder => (
                                        <Option key={holder} value={holder}>
                                            {holder}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* 第六行：备注查询 */}
                    <Row gutter={[16, 8]}>
                        <Col span={24}>
                            <Form.Item
                                name="remarks_keyword"
                                label="备注查询"
                                style={{ marginBottom: 8 }}
                            >
                                <Input
                                    placeholder="输入备注关键词进行模糊搜索..."
                                    allowClear
                                    showCount
                                    maxLength={100}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </>
            )}

            {/* 操作按钮 */}
            <Row>
                <Col span={24}>
                    <Space>
                        <Button 
                            type="primary" 
                            icon={<SearchOutlined />} 
                            onClick={handleFilter}
                            loading={loading}
                        >
                            查询
                        </Button>
                        <Button 
                            icon={<UndoOutlined />} 
                            onClick={handleReset}
                        >
                            重置
                        </Button>
                        <Button 
                            type="link" 
                            onClick={toggleExpanded}
                            style={{ padding: 0 }}
                        >
                            {expanded ? "收起筛选" : "展开筛选"}
                        </Button>
                    </Space>
                </Col>
            </Row>
        </Form>
    );
};

export default UldFilter;
